import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import ModalDialog from '../../components/modal_dialog';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import { StatementImportModal } from './StatementImportModal';
import StatementImportSettingsModal from './StatementImportSettingsModal';
import { formatCard, formatDate, formatPeriod } from './statement-display';
import AccessControl from '../../utils/access_control';
import { statementTablePaperSx, statementTableContainerSx, statementTableSx, statementTableHeaderSx } from '../../components/table/mui-table-styles';

import StatementFilePreview from './StatementFilePreview';

import './statement-imports.scss';

// A statement that is still being processed resolves on its own, so the list
// re-reads itself until nothing is pending. The ceiling keeps a statement that
// never resolves from polling for the life of the page.
const PROCESSING_POLL_INTERVAL_MS = 10 * 1000;
const PROCESSING_POLL_MAX_MS = 10 * 60 * 1000;

const columns = [
  { id: 'filename', label: 'cc_statements.statement_imports.table.filename' },
  { id: 'card', label: 'cc_statements.statement_imports.table.card' },
  { id: 'issuer', label: 'cc_statements.statement_imports.table.issuer' },
  { id: 'period', label: 'cc_statements.statement_imports.table.period' },
  { id: 'items', label: 'cc_statements.statement_imports.table.items', extraction: true },
  { id: 'matched', label: 'cc_statements.statement_imports.table.matched', extraction: true },
  { id: 'status', label: 'cc_statements.statement_imports.table.status' },
  { id: 'uploaded', label: 'cc_statements.statement_imports.table.uploaded' },
];

/**
 * Introduces statement imports and links to the dedicated upload page.
 *
 * @param {object} props
 * @param {object} props.data - MobX data store containing the translator.
 * @returns {JSX.Element} The statement-import call-to-action.
 */
const StatementImportsSection = ({ data }) => {
  const [reconciliations, setReconciliations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [cardFilter, setCardFilter] = useState('');
  const [filenameSearch, setFilenameSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const canEditClientConfig = AccessControl.canAccessAdminSection(data, 'clients');

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadReconciliations = useCallback(() => {
    apiFetch(
      '/c_card/statement-reconciliations',
      'GET',
      {},
      (response) => {
        if (!mountedRef.current) return;
        setReconciliations(response.data?.items || []);
        setLoadFailed(false);
        setLoading(false);
      },
      () => {
        if (!mountedRef.current) return;
        setLoadFailed(true);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    loadReconciliations();
  }, [loadReconciliations]);

  // Processing finishes on the API's own schedule with nothing to notify us, so
  // the list re-reads itself while any row is still pending and stops as soon
  // as none is.
  const hasProcessing = reconciliations.some(
    (reconciliation) => reconciliation.status === 'processing'
  );

  useEffect(() => {
    if (!hasProcessing) return undefined;

    const interval = setInterval(loadReconciliations, PROCESSING_POLL_INTERVAL_MS);
    const ceiling = setTimeout(() => clearInterval(interval), PROCESSING_POLL_MAX_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(ceiling);
    };
  }, [hasProcessing, loadReconciliations]);

  // Summarize the same imports shown below; other statuses only contribute to uploaded.
  const summaryCounts = {
    uploaded: reconciliations.length,
    ready: reconciliations.filter((item) => item.status === 'ready').length,
    failed: reconciliations.filter((item) => item.status === 'failed').length,
  };

  // Options come only from server-authorized imports; filters never grant access.
  const cardOptions = [...new Map(reconciliations.filter((item) => item.card_account?.id)
    .map((item) => [item.card_account.id, item.card_account])).values()];
  const hasUnidentified = reconciliations.some((item) => !item.card_account?.id);
  const filteredReconciliations = reconciliations.filter((item) => (
    (!cardFilter || (item.card_account?.id || 'unidentified') === cardFilter)
    && (item.filename || '').toLowerCase().includes(filenameSearch.trim().toLowerCase())
  ));
  // Tab counts reflect card/search filters, before the status filter is applied.
  const statuses = ['all', 'ready', 'failed', 'processing', 'reconciled'];
  const statusCounts = Object.fromEntries(statuses.map((status) => [status,
    status === 'all' ? filteredReconciliations.length
      : filteredReconciliations.filter((item) => item.status === status).length,
  ]));
  const availableStatuses = statuses.filter((status) => !['processing', 'reconciled'].includes(status)
    || reconciliations.some((item) => item.status === status));
  const activeStatus = availableStatuses.includes(statusFilter) ? statusFilter : 'all';
  const visibleReconciliations = filteredReconciliations
    .filter((item) => activeStatus === 'all' || item.status === activeStatus)
    .sort((a, b) => {
      const first = Date.parse(a.created_at);
      const second = Date.parse(b.created_at);
      // Undated imports stay at the end in either direction.
      if (!Number.isFinite(first)) return Number.isFinite(second) ? 1 : 0;
      if (!Number.isFinite(second)) return -1;
      return sortOrder === 'oldest' ? first - second : second - first;
    });
  const visibleIdsKey = JSON.stringify(visibleReconciliations.map((item) => item.id));
  useEffect(() => {
    // Polling can move a selected import out of the active status tab.
    const visibleIds = JSON.parse(visibleIdsKey);
    setSelectedIds((current) => current.every((id) => visibleIds.includes(id))
      ? current : current.filter((id) => visibleIds.includes(id)));
  }, [visibleIdsKey]);

  const updateFilters = (card, filename) => {
    setCardFilter(card);
    setFilenameSearch(filename);
    // Avoid deleting selected imports that a filter has hidden from the user.
    setSelectedIds([]);
  };

  const selectableIds = visibleReconciliations.map((reconciliation) => reconciliation.id);
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleOne = (id, checked) => {
    setSelectedIds((current) => (
      checked ? [...current, id] : current.filter((value) => value !== id)
    ));
  };

  const toggleAll = (checked) => setSelectedIds(checked ? selectableIds : []);

  /** Soft-delete the selection in one request, the way the sibling tables do. */
  const deleteSelected = () => {
    setDeleting(true);

    apiFetch(
      '/c_card/statement-reconciliations',
      'DELETE',
      { reconciliation_ids: selectedIds },
      () => {
        setReconciliations((current) => current.filter((item) => !selectedIds.includes(item.id)));
        setSelectedIds([]);
        setDeleting(false);
        setConfirmingDelete(false);
        toast.success(data.t.read('cc_statements.statement_imports.delete_successful'));
      },
      () => {
        setDeleting(false);
        setConfirmingDelete(false);
        toast.error(data.t.read('cc_statements.statement_imports.delete_failed'));
      }
    );
  };

  return (
    <section className='statement-imports mb-5' aria-labelledby='statement-imports-title'>
      <div className='statement-imports__header'>
        <div>
          <h2 id='statement-imports-title'>{data.t.read('cc_statements.reconciliation')}</h2>
          <p>{data.t.read('cc_statements.statement_imports.description')}</p>
        </div>
        <div className='statement-imports__header-actions'>
          {canEditClientConfig && (
            <button type='button' className='statement-imports__settings-button'
              onClick={() => setShowSettings(true)}>
              <FontAwesomeIcon icon={faSlidersH} aria-hidden='true' />
              {data.t.read('cc_statements.statement_imports.settings_button')}
            </button>
          )}
          <Link className='statement-imports__upload-button' to='/c_card/statements/add'>
            <FontAwesomeIcon icon={faUpload} aria-hidden='true' />
            {data.t.read('cc_statements.statement_imports.upload_button')}
          </Link>
        </div>
      </div>
      <ul className='statement-imports__summary' aria-label={data.t.read('cc_statements.statement_imports.summary.label')}>
        {Object.entries(summaryCounts).map(([key, count]) => (
          <li key={key}>
            <span className='statement-imports__summary-label'>
              {data.t.read(`cc_statements.statement_imports.summary.${key}`)}
            </span>
            <span className='statement-imports__summary-count'>{loading || loadFailed ? '-' : count}</span>
          </li>
        ))}
      </ul>
      <div className='statement-imports__filters'>
        <label htmlFor='statement-import-card'>
          {data.t.read('cc_statements.statement_imports.filters.card')}
          <select id='statement-import-card' value={cardFilter}
            onChange={(event) => updateFilters(event.target.value, filenameSearch)}>
            <option value=''>{data.t.read('cc_statements.statement_imports.filters.all_cards')}</option>
            {cardOptions.map((card) => <option key={card.id} value={card.id}>{formatCard(card)}</option>)}
            {hasUnidentified && <option value='unidentified'>{data.t.read('cc_statements.statement_imports.filters.unidentified')}</option>}
          </select>
        </label>
        <label htmlFor='statement-import-search'>
          {data.t.read('cc_statements.statement_imports.filters.search')}
          <input id='statement-import-search' type='search' value={filenameSearch}
            placeholder={data.t.read('cc_statements.statement_imports.filters.placeholder')}
            onChange={(event) => updateFilters(cardFilter, event.target.value)} />
        </label>
        <button type='button' onClick={() => { updateFilters('', ''); setStatusFilter('all'); }}>
          {data.t.read('cc_statements.statement_imports.filters.reset')}
        </button>
      </div>
      <div className='statement-imports__toolbar'>
        <Tabs value={activeStatus} variant='scrollable' scrollButtons='auto'
          aria-label={data.t.read('cc_statements.statement_imports.toolbar.status')}
          onChange={(event, value) => { setStatusFilter(value); setSelectedIds([]); }}>
          {availableStatuses.map((status) => (
            <Tab key={status} value={status} label={`${data.t.read(status === 'all'
              ? 'cc_statements.statement_imports.toolbar.all'
              : `cc_statements.statement_imports.status.${status}`)} ${statusCounts[status]}`} />
          ))}
        </Tabs>
        <div className='statement-imports__toolbar-actions'>
          <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}
            aria-label={data.t.read('cc_statements.statement_imports.toolbar.sort')}>
            <option value='newest'>{data.t.read('cc_statements.statement_imports.toolbar.newest')}</option>
            <option value='oldest'>{data.t.read('cc_statements.statement_imports.toolbar.oldest')}</option>
          </select>
          <span aria-live='polite'>{selectedIds.length} {data.t.read('cc_statements.statement_imports.toolbar.selected')}</span>
          <button
            type='button'
            className='statement-imports__delete-button'
            disabled={selectedIds.length === 0 || deleting}
            onClick={() => setConfirmingDelete(true)}
          >
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_statements.statement_imports.delete_selected')}
          </button>
        </div>
      </div>

      <Paper elevation={0} sx={statementTablePaperSx}>
        <TableContainer sx={statementTableContainerSx}>
          <Table
            aria-label={data.t.read('cc_statements.statement_imports.table.label')}
            sx={statementTableSx}
          >
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' rowSpan={2} sx={statementTableHeaderSx}>
                  <Checkbox
                    color='primary'
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={selectableIds.length === 0}
                    inputProps={{
                      'aria-label': data.t.read('cc_statements.statement_imports.select_all'),
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleAll(event.target.checked)}
                  />
                </TableCell>
                {columns.map((column) => column.extraction ? (
                  column.id === 'items' && <TableCell key='extraction' colSpan={2} scope='colgroup' align='center'
                    sx={{ ...statementTableHeaderSx, backgroundColor: '#fff7e9', color: '#a65b00', py: '6px' }}>
                    {data.t.read('cc_statements.statement_imports.table.extraction_results')}
                  </TableCell>
                ) : (
                  <TableCell key={column.id} rowSpan={2} scope='col' sx={statementTableHeaderSx}>
                    {data.t.read(column.label)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.filter((column) => column.extraction).map((column) => (
                  <TableCell key={column.id} scope='col' align='center'
                    title={data.t.read(`cc_statements.statement_imports.table.${column.id}_help`)}
                    sx={{ ...statementTableHeaderSx, backgroundColor: '#fff7e9', py: '6px' }}>
                    {data.t.read(column.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <div className='d-flex justify-content-center'>
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && loadFailed && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center' role='alert'>
                    {data.t.read('cc_statements.statement_imports.table.load_failed')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadFailed && visibleReconciliations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center'>
                    {data.t.read(reconciliations.length ? 'cc_statements.statement_imports.filters.empty' : 'cc_statements.statement_imports.table.empty')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadFailed && visibleReconciliations.map((reconciliation) => {
                // A statement still being processed has no lines, no card and no
                // matches yet, so there is nothing to open: the row stays inert
                // until the import resolves.
                const isProcessing = reconciliation.status === 'processing';

                return (
                  <TableRow
                    key={reconciliation.id}
                    hover={!isProcessing}
                    tabIndex={isProcessing ? undefined : 0}
                    role={isProcessing ? undefined : 'button'}
                    aria-label={reconciliation.filename || reconciliation.id}
                    aria-disabled={isProcessing || undefined}
                    className={`statement-imports__row${isProcessing ? ' statement-imports__row--processing' : ''}`}
                    onClick={isProcessing ? undefined : () => setSelectedReconciliation(reconciliation)}
                    onKeyDown={(event) => {
                      if (isProcessing) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      // Space belongs to the checkbox when it has focus; it must
                      // not also open the statement behind it.
                      if (event.target.type === 'checkbox') return;
                      event.preventDefault();
                      setSelectedReconciliation(reconciliation);
                    }}
                  >
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={selectedIds.includes(reconciliation.id)}
                        inputProps={{
                          'aria-label': data.t.read('cc_statements.statement_imports.select_row'),
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => toggleOne(reconciliation.id, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <StatementFilePreview statement={reconciliation} onOpen={() => setSelectedReconciliation(reconciliation)} />
                    </TableCell>
                    <TableCell>{formatCard(reconciliation.card_account)}</TableCell>
                    <TableCell>{reconciliation.issuer_name || '—'}</TableCell>
                    <TableCell>{formatPeriod(reconciliation.statement)}</TableCell>
                    {/* Preserve confirmed zeros; unknown or in-progress extraction shows a dash. */}
                    <TableCell align='center'>{isProcessing ? '-' : reconciliation.item_count ?? '-'}</TableCell>
                    <TableCell align='center'>{isProcessing ? '-' : reconciliation.matching?.matched_count ?? '-'}</TableCell>
                    <TableCell>
                      <span className={`statement-imports__status statement-imports__status--${reconciliation.status}`}>
                        {data.t.read(`cc_statements.statement_imports.status.${reconciliation.status}`)}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(reconciliation.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {confirmingDelete && (
        <ModalDialog
          variant='confirm'
          destructive
          title={data.t.read('cc_statements.statement_imports.delete_title')}
          subtitle={`${data.t.read('cc_statements.statement_imports.delete_confirm_message')} (${selectedIds.length})`}
          cancelAction={() => setConfirmingDelete(false)}
          cancelText={data.t.read('cancel')}
          okAction={() => {
            deleteSelected();
            // Held open by `deleting` until the request answers.
            return false;
          }}
          okText={data.t.read('delete')}
          okLoading={deleting}
          invalid={deleting}
          noProcessingLoading
        />
      )}

      {selectedReconciliation && (
        <StatementImportModal
          data={data}
          reconciliation={selectedReconciliation}
          onClose={() => setSelectedReconciliation(null)}
        />
      )}

      {showSettings && <StatementImportSettingsModal data={data} onClose={() => setShowSettings(false)} />}
    </section>
  );
};

export default StatementImportsSection;
